import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { get } from 'lodash';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { transformConfiguration } from '../helpers';
import { FilterCategoryV2, FilterConfigurationV2, FilterEngineOptions } from '../models';

/**
 * Service for loading and transforming filter configuration
 * Handles HTTP requests, caching, and coordination with settings service
 */
@Injectable({ providedIn: 'root' })
export class FilterConfigurationService {
  // State signals
  readonly configuration: WritableSignal<FilterConfigurationV2 | null> = signal(null);
  readonly categories: WritableSignal<FilterCategoryV2[]> = signal([]);
  readonly isLoading: WritableSignal<boolean> = signal(false);
  readonly error: WritableSignal<string | null> = signal(null);

  // Dependencies
  private readonly http = inject(HttpClient);

  // Cache for configuration requests
  private readonly configurationCache = new Map<string, Observable<FilterCategoryV2[]>>();

  /**
   * Get filter configuration for a specific master view
   * Results are cached to avoid redundant HTTP requests
   *
   * @param masterView - Master view identifier (e.g., 'devices', 'sensors')
   * @returns Observable of filter categories
   */
  getConfiguration(masterView: string): Observable<FilterCategoryV2[]> {
    // Return cached request if available
    if (this.configurationCache.has(masterView)) {
      return this.configurationCache.get(masterView) as Observable<FilterCategoryV2[]>;
    }

    // Create new request with shareReplay to enable caching
    const request$ = this.http.get<FilterCategoryV2[]>(`/assets/engines/filters/filter-criteria-${masterView}.json`).pipe(shareReplay(1));

    // Cache the request
    this.configurationCache.set(masterView, request$);

    return request$;
  }

  transformConfiguration(rawCategories: any[], masterView: string, options: FilterEngineOptions): FilterConfigurationV2 {
    const masterViewSettings = get(options, masterView);
    return transformConfiguration(rawCategories, masterViewSettings);
  }
}
